import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { explorerTxUrl } from '../network';

function displayPoolName(recipientAddress) {
    // "Pool: State: California" → "California"
    // "Pool: City: New York, NY" → "New York, NY"
    // "Pool: Industry: Healthcare" → "Healthcare"
    // "Pool: United States" → "United States"
    return recipientAddress
        .replace(/^Pool: /, '')
        .replace(/^(State|City|Industry): /, '')
        .replace('You Bee General Pool', 'You Bee General Hive');
}

// Format pool_label (raw pool name from DB) into a human-readable source string
// e.g. "Industry: Information Technology & Software"
//   → "the Information Technology & Software industry pool"
function formatUsd(usd) {
    if (!usd || usd === 0) return '$0.00';
    for (let d = 2; d <= 8; d++) {
        if (parseFloat(usd.toFixed(d)) !== 0) {
            return `$${usd.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d })}`;
        }
    }
    return '<$0.000000001';
}

function formatPoolSource(poolLabel) {
    if (!poolLabel) return 'a hive';
    if (poolLabel === 'You Bee General Pool') return 'the You Bee General Hive';
    if (poolLabel.startsWith('Industry: ')) return `the ${poolLabel.slice('Industry: '.length)} industry hive`;
    if (poolLabel.startsWith('State: '))    return `the ${poolLabel.slice('State: '.length)} state hive`;
    if (poolLabel.startsWith('City: '))     return `the ${poolLabel.slice('City: '.length)} city hive`;
    return `the ${poolLabel}`;
}

function ActivityFeed({ cardStyle, solPrice }) {
    const { user } = useAuth();
    const [txs, setTxs] = useState([]);
    const [allDist, setAllDist] = useState([]);
    const [myDist, setMyDist] = useState([]);
    const [allContributions, setAllContributions] = useState([]);
    const [allGiveBacks, setAllGiveBacks] = useState([]);
    const [distFilter, setDistFilter] = useState('all');
    const [contribFilter, setContribFilter] = useState('all');

    const fetchTransactions = async () => {
        try {
            const res = await api.get('/ledger');
            setTxs(res.data);
        } catch (err) {
            console.error("Failed to fetch activity");
        }
    };

    const fetchAllDistributions = async () => {
        try {
            const res = await api.get('/distributions');
            setAllDist(res.data);
        } catch (err) {
            console.error("Failed to fetch distributions");
        }
    };

    const fetchMyDistributions = async () => {
        try {
            const res = await api.get('/my-distributions');
            setMyDist(res.data);
        } catch (err) {
            console.error("Failed to fetch my distributions");
        }
    };

    const fetchAllContributions = async () => {
        try {
            const res = await api.get('/contributions');
            setAllContributions(res.data);
        } catch (err) {
            console.error("Failed to fetch contributions");
        }
    };

    const fetchAllGiveBacks = async () => {
        try {
            const res = await api.get('/givebacks');
            setAllGiveBacks(res.data);
        } catch (err) {
            console.error("Failed to fetch give-backs");
        }
    };

    useEffect(() => {
        fetchTransactions();
        fetchAllDistributions();
        fetchMyDistributions();
        fetchAllContributions();
        fetchAllGiveBacks();
        const interval = setInterval(() => {
            fetchTransactions();
            fetchAllDistributions();
            fetchMyDistributions();
            fetchAllContributions();
            fetchAllGiveBacks();
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    const txUsd = (tx) => tx.usd_value != null
        ? parseFloat(tx.usd_value)
        : solPrice ? parseFloat(tx.amount) * solPrice : null;

    const since24h = Date.now() - 24 * 60 * 60 * 1000;

    // All contributions: public endpoint + give-backs, last 24 hours only
    const contributionsAll = [
        ...allContributions.filter(tx => {
            const usd = txUsd(tx);
            return (usd === null || usd >= 0.10) && new Date(tx.timestamp).getTime() >= since24h;
        }),
        ...allGiveBacks.filter(gb => new Date(gb.timestamp).getTime() >= since24h).map(gb => ({ ...gb, _isGiveBack: true })),
    ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).slice(0, 10);

    // Mine: current user's donations from ledger + their give-backs, last 24 hours only
    const myDonations = txs.filter(tx => {
        if (!tx.recipient_address || !tx.recipient_address.startsWith('Pool: ')) return false;
        if (tx.user_id !== user?.id) return false;
        const usd = txUsd(tx);
        return (usd === null || usd >= 0.10) && new Date(tx.timestamp).getTime() >= since24h;
    });
    const contributionsMine = [...myDonations, ...allGiveBacks.filter(gb => gb.user_id === user?.id && new Date(gb.timestamp).getTime() >= since24h).map(gb => ({ ...gb, _isGiveBack: true }))]
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        .slice(0, 10);

    const contributions = contribFilter === 'all' ? contributionsAll : contributionsMine;

    // All Distributions: aggregate records from /distributions (visible to everyone)
    const distributionsAll = allDist.slice(0, 10);

    // My Distributions: from dedicated /my-distributions endpoint (always filtered by user_id).
    // Aggregate by pool + hour so duplicate chunk records collapse into one entry.
    const distributionsMine = (() => {
        const buckets = {};
        for (const tx of myDist) {
            const hour = new Date(tx.timestamp).toISOString().slice(0, 13);
            const key = `${tx.pool_label || tx.note || ''}|${hour}`;
            if (!buckets[key]) {
                buckets[key] = { ...tx, usd_value: 0, amount: 0 };
            }
            buckets[key].usd_value += parseFloat(tx.usd_value ?? tx.amount ?? 0);
            buckets[key].amount    += parseFloat(tx.amount ?? 0);
        }
        return Object.values(buckets)
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
            .slice(0, 10);
    })();

    const currentDistributions = distFilter === 'all' ? distributionsAll : distributionsMine;

    const tsStyle = { color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.75rem', marginTop: '6px' };

    const colHeader = (label) => (
        <h4 style={{
            margin: '0 0 16px',
            color: '#ffc107',
            fontSize: '0.85rem',
            textTransform: 'uppercase',
            letterSpacing: '1.5px',
            fontWeight: 600,
        }}>
            {label}
        </h4>
    );

    const colStyle = {
        flex: 1,
        minWidth: 0,
        borderRadius: '12px',
        background: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        padding: '16px',
    };

    const itemStyle = {
        borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
        padding: '12px 0',
        fontSize: '0.875rem',
        color: 'rgba(255, 255, 255, 0.85)',
    };

    const explorerLink = (txHash) => txHash && txHash.length > 20 && (
        <> · <a
            href={explorerTxUrl(txHash)}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'rgba(255,193,7,0.6)', fontSize: '0.72rem' }}
        >Explorer ↗</a></>
    );

    return (
        <div style={{
            background: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '20px',
            backdropFilter: 'blur(20px)',
            padding: '24px',
            ...cardStyle,
        }}>
            <h3 style={{
                marginTop: 0,
                marginBottom: '24px',
                color: '#ffc107',
                fontSize: '1.25rem',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
            }}>
                <span style={{ fontSize: '1.1rem' }}>Live Ledger</span>
            </h3>

            <style>{`
                .activity-feed-columns {
                    display: flex;
                    gap: 20px;
                    flex-wrap: wrap;
                }
                .activity-feed-divider {
                    width: 1px;
                    background: rgba(255, 255, 255, 0.08);
                    align-self: stretch;
                    flex-shrink: 0;
                }
                @media (max-width: 700px) {
                    .activity-feed-columns {
                        flex-direction: column;
                    }
                    .activity-feed-divider {
                        display: none;
                    }
                }
            `}</style>
            <div className="activity-feed-columns">

                {/* --- CONTRIBUTIONS --- */}
                <div style={colStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                        <h4 style={{ margin: 0, color: '#ffc107', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: 600 }}>
                            Contributions
                        </h4>
                        <div style={{ display: 'flex', gap: '6px' }}>
                            {[['all', 'All'], ['mine', 'Mine']].map(([key, label]) => (
                                <button
                                    key={key}
                                    onClick={() => setContribFilter(key)}
                                    style={{
                                        padding: '4px 12px', borderRadius: '20px',
                                        border: contribFilter === key ? '1px solid rgba(255,193,7,0.5)' : '1px solid rgba(255,255,255,0.15)',
                                        background: contribFilter === key ? 'rgba(255,193,7,0.2)' : 'rgba(255,255,255,0.05)',
                                        color: contribFilter === key ? '#ffc107' : 'rgba(255,255,255,0.6)',
                                        fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer', transition: 'all 0.2s',
                                    }}
                                >{label}</button>
                            ))}
                        </div>
                    </div>
                    {contributions.length === 0 ? (
                        <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.85rem' }}>
                            {contribFilter === 'mine' ? 'No contributions from you in the last 24 hours.' : 'No contributions in the last 24 hours.'}
                        </p>
                    ) : (
                        contributions.map(tx => {
                            if (tx._isGiveBack) {
                                const sol = Math.abs(parseFloat(tx.amount));
                                const usdDisplay = tx.usd_value != null
                                    ? formatUsd(parseFloat(tx.usd_value))
                                    : solPrice ? formatUsd(sol * solPrice) : null;
                                return (
                                    <div key={tx.id} style={itemStyle}>
                                        <div>
                                            <strong style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Anonymous</strong>
                                            {' gave back '}
                                            <strong style={{ color: '#22c55e' }}>
                                                {usdDisplay ?? `${sol.toFixed(6)} SOL`}
                                            </strong>
                                            {' to the '}
                                            <span style={{ color: '#ffc107', fontWeight: 600 }}>You Bee General Hive!</span>
                                            {' 🐝'}
                                        </div>
                                        <div style={tsStyle}>
                                            {new Date(tx.timestamp).toLocaleString()}
                                            {explorerLink(tx.tx_hash)}
                                        </div>
                                    </div>
                                );
                            }
                            const poolName = displayPoolName(tx.recipient_address);
                            const donor = tx.user_name || 'A You Bee Worker';
                            return (
                                <div key={tx.id} style={itemStyle}>
                                    <div>
                                        <strong style={{ color: 'rgba(255, 255, 255, 0.9)' }}>{donor}</strong>
                                        {' contributed '}
                                        <strong style={{ color: '#22c55e' }}>
                                            {tx.usd_value != null
                                                ? formatUsd(parseFloat(tx.usd_value))
                                                : solPrice
                                                    ? formatUsd(parseFloat(tx.amount) * solPrice)
                                                    : `${parseFloat(tx.amount).toFixed(4)} SOL`}
                                        </strong>
                                        {' to '}
                                        <Link
                                            to="/pools"
                                            style={{ color: '#ffc107', textDecoration: 'none', fontWeight: 600 }}
                                        >
                                            {poolName}
                                        </Link>
                                    </div>
                                    <div style={tsStyle}>
                                        {new Date(tx.timestamp).toLocaleString()}
                                        {explorerLink(tx.tx_hash)}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Divider */}
                <div className="activity-feed-divider" />

                {/* --- DISTRIBUTIONS --- */}
                <div style={colStyle}>
                    {/* Header + filter toggle */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                        <h4 style={{
                            margin: 0,
                            color: '#ffc107',
                            fontSize: '0.85rem',
                            textTransform: 'uppercase',
                            letterSpacing: '1.5px',
                            fontWeight: 600,
                        }}>
                            Distributions
                        </h4>
                        <div style={{ display: 'flex', gap: '6px' }}>
                            {[['all', 'All'], ['mine', 'Mine']].map(([key, label]) => (
                                <button
                                    key={key}
                                    onClick={() => setDistFilter(key)}
                                    style={{
                                        padding: '4px 12px',
                                        borderRadius: '20px',
                                        border: distFilter === key
                                            ? '1px solid rgba(255, 193, 7, 0.5)'
                                            : '1px solid rgba(255,255,255,0.15)',
                                        background: distFilter === key
                                            ? 'rgba(255, 193, 7, 0.2)'
                                            : 'rgba(255,255,255,0.05)',
                                        color: distFilter === key ? '#ffc107' : 'rgba(255,255,255,0.6)',
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                    }}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {currentDistributions.length === 0 ? (
                        <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.85rem' }}>
                            {distFilter === 'mine'
                                ? 'No distributions to your wallet yet.'
                                : 'No distributions yet.'}
                        </p>
                    ) : distFilter === 'all' ? (
                        currentDistributions.map(tx => {
                            const poolLabel = tx.recipient_address.replace(/^Distribution: /, '').replace('You Bee General Pool', 'You Bee General Hive');
                            const amtDisplay = tx.usd_value != null
                                ? formatUsd(parseFloat(tx.usd_value))
                                : solPrice ? formatUsd(parseFloat(tx.amount) * solPrice) : `${parseFloat(tx.amount).toFixed(6)} SOL`;
                            return (
                                <div key={tx.id} style={itemStyle}>
                                    <div>
                                        <strong style={{ color: '#ffc107' }}>{poolLabel}</strong>
                                        {' distributed '}
                                        <strong style={{ color: '#22c55e' }}>{amtDisplay}</strong>
                                    </div>
                                    <div style={tsStyle}>
                                        {new Date(tx.timestamp).toLocaleString()}
                                        {explorerLink(tx.tx_hash)}
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        currentDistributions.map(tx => {
                            const amtDisplay = tx.usd_value != null
                                ? formatUsd(parseFloat(tx.usd_value))
                                : solPrice ? formatUsd(parseFloat(tx.amount) * solPrice) : `${parseFloat(tx.amount).toFixed(6)} SOL`;
                            return (
                                <div key={tx.id} style={itemStyle}>
                                    <div>
                                        {'Received '}
                                        <strong style={{ color: '#22c55e' }}>{amtDisplay}</strong>
                                        {' from '}
                                        <span style={{ color: '#ffc107', fontWeight: 600 }}>
                                            {formatPoolSource(tx.pool_label)}
                                        </span>
                                    </div>
                                    <div style={tsStyle}>
                                        {new Date(tx.timestamp).toLocaleString()}
                                        {explorerLink(tx.tx_hash)}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
}

export default ActivityFeed;
